/**
 * Form ↔ wire conversion for the daily earning stages.
 *
 * The wire is integers only: stage bounds in SECONDS, rates in PAISE per
 * minute. The form speaks hours and rupees. Each stage's start is not an input
 * at all — it is the previous stage's end (0 for the first) — and the last
 * stage has no end (unlimited), so the form cannot express a gap, an overlap
 * or a second unlimited stage. The server re-validates everything.
 *
 * There are up to FOUR stage tables, one per progression:
 *   stages          — Online (the original list; always present)
 *   audioStages     — Audio/Video Separate: audio call time
 *   videoStages     — Audio/Video Separate: video call time
 *   combinedStages  — Audio/Video Combined: audio + video call time
 * A table the server has not stored yet starts as a COPY of the online stages
 * (never a blank), and every table is sent back on save so switching basis
 * never loses one. Only the tables the chosen basis runs are shown/validated.
 *
 * These are LISTENER earnings only. The caller's coin price is not part of this
 * config and nothing here can change it.
 */
import { toPaise, toRupees } from './callPricingForm';

export interface OnlineEarningStage {
  fromSeconds: number;
  toSeconds: number | null;
  directVideoEarnPaise: number;
  directAudioEarnPaise: number;
  randomVideoEarnPaise: number;
  randomAudioEarnPaise: number;
}

/** Which time feeds the stages — and therefore which table(s) are used. */
export type EarningBasis = 'online' | 'audio_video_separate' | 'audio_video_combined';

export const BASIS_OPTIONS: ReadonlyArray<{ value: EarningBasis; label: string }> = [
  { value: 'online', label: 'Online' },
  { value: 'audio_video_separate', label: 'Audio/Video Separate' },
  { value: 'audio_video_combined', label: 'Audio/Video Combined' },
];

/** Values a short-lived earlier build stored; mapped to the nearest of the three. */
const LEGACY_BASIS: Record<string, EarningBasis> = {
  all_calls: 'audio_video_combined',
  video_call: 'audio_video_separate',
  audio_call: 'audio_video_separate',
};

export function normalizeBasis(raw: string | undefined): EarningBasis {
  if (!raw) return 'online';
  if (raw === 'online' || raw === 'audio_video_separate' || raw === 'audio_video_combined') return raw;
  return LEGACY_BASIS[raw] ?? 'online';
}

/** The stage-table keys, on the wire and in the form alike. */
export type StageListName = 'stages' | 'audioStages' | 'videoStages' | 'combinedStages';

/** A rate column of a stage row. */
export type RateCol = 'dv' | 'da' | 'rv' | 'ra';

/** One table the form shows: which list, its heading, and which rate columns matter. */
export interface StageTableSpec {
  list: StageListName;
  title: string;
  /** "hours online" / "hours on audio calls" — the From/To column unit. */
  hours: string;
  /** Which rate columns this progression actually uses. */
  cols: RateCol[];
}

/** The tables a basis runs, in display order. Nothing else is shown. */
/**
 * The stage tables the basis runs and the rate columns each SHOWS. Only direct
 * rates are shown: random calls are retired. The random rate fields stay in the
 * rows (and are saved back unchanged), so an old config keeps its numbers.
 */
export function tablesFor(basis: EarningBasis | undefined): StageTableSpec[] {
  switch (normalizeBasis(basis)) {
    case 'audio_video_separate':
      return [
        { list: 'audioStages', title: 'Audio call stages', hours: 'hours on audio calls', cols: ['da'] },
        { list: 'videoStages', title: 'Video call stages', hours: 'hours on video calls', cols: ['dv'] },
      ];
    case 'audio_video_combined':
      return [{ list: 'combinedStages', title: 'Combined call stages', hours: 'hours on calls', cols: ['dv', 'da'] }];
    default:
      return [{ list: 'stages', title: 'Stages', hours: 'hours online', cols: ['dv', 'da'] }];
  }
}

export interface OnlineEarningConfig {
  enabled: boolean;
  /** Absent on a config saved before it existed — reads as `online`. */
  basis?: EarningBasis;
  timezone: string;
  /** Minutes after local midnight; 480 = 08:00. */
  dayStartMinute: number;
  /** The Online stages (the original list). */
  stages: OnlineEarningStage[];
  /** Per-track tables; a missing one inherits `stages` on the server. */
  audioStages?: OnlineEarningStage[];
  videoStages?: OnlineEarningStage[];
  combinedStages?: OnlineEarningStage[];
}

/** One stage row in the form: its END in hours (null on the last row), ₹/min. */
export interface StageRow {
  toHours: number | null;
  dv: number;
  da: number;
  rv: number;
  ra: number;
}

export interface OnlineEarningForm {
  enabled: boolean;
  /** Missing = Online (a form built without it). */
  basis?: EarningBasis;
  timezone: string;
  /** "HH:MM" */
  dayStart: string;
  stages: StageRow[];
  audioStages: StageRow[];
  videoStages: StageRow[];
  combinedStages: StageRow[];
}

const SECONDS_PER_HOUR = 3600;

export const hoursToSeconds = (h: number) => Math.round(h * SECONDS_PER_HOUR);
export const secondsToHours = (s: number) => s / SECONDS_PER_HOUR;

export const minuteToHhmm = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

export function hhmmToMinute(v: string): number {
  const [h = 0, m = 0] = v.split(':').map((x) => Number.parseInt(x, 10));
  return h * 60 + m;
}

const rowsFrom = (list: OnlineEarningStage[]): StageRow[] =>
  list.map((s) => ({
    toHours: s.toSeconds === null ? null : secondsToHours(s.toSeconds),
    dv: toRupees(s.directVideoEarnPaise),
    da: toRupees(s.directAudioEarnPaise),
    rv: toRupees(s.randomVideoEarnPaise),
    ra: toRupees(s.randomAudioEarnPaise),
  }));

/** Server config → form values. A table the server lacks starts as a COPY of the online stages. */
export function formFromConfig(cfg: OnlineEarningConfig): OnlineEarningForm {
  const online = rowsFrom(cfg.stages);
  const copy = () => online.map((r) => ({ ...r }));
  return {
    enabled: cfg.enabled,
    basis: normalizeBasis(cfg.basis),
    timezone: cfg.timezone,
    dayStart: minuteToHhmm(cfg.dayStartMinute),
    stages: online,
    audioStages: cfg.audioStages ? rowsFrom(cfg.audioStages) : copy(),
    videoStages: cfg.videoStages ? rowsFrom(cfg.videoStages) : copy(),
    combinedStages: cfg.combinedStages ? rowsFrom(cfg.combinedStages) : copy(),
  };
}

/** Rows → wire stages. Starts chain from the previous end; the last stage is unlimited. */
function stagesFromRows(rows: StageRow[]): OnlineEarningStage[] {
  let from = 0;
  return rows.map((row, i) => {
    const last = i === rows.length - 1;
    const to = last || row.toHours == null ? null : hoursToSeconds(row.toHours);
    const stage: OnlineEarningStage = {
      fromSeconds: from,
      toSeconds: last ? null : to,
      directVideoEarnPaise: toPaise(row.dv),
      directAudioEarnPaise: toPaise(row.da),
      randomVideoEarnPaise: toPaise(row.rv),
      randomAudioEarnPaise: toPaise(row.ra),
    };
    if (stage.toSeconds !== null) from = stage.toSeconds;
    return stage;
  });
}

/** The PUT body. EVERY table is sent, so switching basis never drops one. */
export function configFromForm(f: OnlineEarningForm): OnlineEarningConfig {
  const basis = normalizeBasis(f.basis);
  return {
    enabled: f.enabled,
    // `online` is what a missing basis already means, so it is left out — an
    // Online config is stored exactly as it was before the basis existed.
    ...(basis !== 'online' ? { basis } : {}),
    timezone: f.timezone.trim(),
    dayStartMinute: hhmmToMinute(f.dayStart),
    stages: stagesFromRows(f.stages),
    audioStages: stagesFromRows(f.audioStages ?? []),
    videoStages: stagesFromRows(f.videoStages ?? []),
    combinedStages: stagesFromRows(f.combinedStages ?? []),
  };
}

/**
 * Problems the operator can fix before saving, for the tables the basis RUNS;
 * the server checks the same and more. Each non-last stage must end after it
 * starts.
 */
export function formProblems(f: OnlineEarningForm): string[] {
  const out: string[] = [];
  for (const t of tablesFor(f.basis)) {
    const rows = f[t.list] ?? [];
    const name = t.list === 'stages' ? 'Stage' : `${t.title.replace(' stages', '')} stage`;
    if (f.enabled && rows.length === 0) out.push(`${t.title}: add at least one stage before enabling.`);
    let fromH = 0;
    rows.forEach((row, i) => {
      if (i === rows.length - 1) return;
      if (row.toHours == null) {
        out.push(`${name} ${i + 1}: set where it ends (only the last stage is unlimited).`);
        return;
      }
      if (row.toHours <= fromH) out.push(`${name} ${i + 1}: must end after ${fromH} h.`);
      fromH = row.toHours;
    });
  }
  return out;
}

/** "HH:MM" every 30 minutes, plus `current` if it is off that grid. */
export function dayStartOptions(current?: string): string[] {
  const grid = Array.from({ length: 48 }, (_, i) => minuteToHhmm(i * 30));
  return current && !grid.includes(current) ? [...grid, current].sort() : grid;
}

/** "08:00" → "8:00 AM", "00:30" → "12:30 AM", "13:00" → "1:00 PM". Label only. */
export function to12h(hhmm: string): string {
  const m = hhmmToMinute(hhmm);
  const h = Math.floor(m / 60);
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m % 60).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}
